/**
 * Ops Status Service
 *
 * Backs the hidden /ops/status page — a lightweight, at-a-glance view of
 * CI pipeline status, Security Dashboard vulnerabilities, and SBOM/
 * dependency-list availability for the Flow ecosystem's GitLab-hosted repos.
 *
 * All calls go through mia-flow-bff's existing GitLab OAuth proxy
 * (routes/gitlab-oauth.js), the same cookie-session-based pattern every
 * other GitLab call in this app uses (see UnifiedGitDataService.js) — no
 * GitLab token is ever held or pasted in the browser. The BFF holds the
 * caller's linked GitLab token server-side and normalizes Ultimate-tier-only
 * endpoints (vulnerabilities, dependencies) into { available: false } instead
 * of erroring when the instance/tier doesn't support them.
 */

import config from '../config';
import { getBffHeaders } from '../utils/apiHeaders';
import { getAuthorizedFetch } from '../utils/fetchRegistry';
import { devLog } from '../utils/devLog';

const GITLAB_BASE_PATH = '/api/gitlab';

async function gitlabGet(path, params) {
  const query = params ? `?${new URLSearchParams(params)}` : '';
  const url = `${config.bffUrl}${GITLAB_BASE_PATH}${path}${query}`;

  const response = await getAuthorizedFetch()(url, {
    credentials: 'include',
    headers: getBffHeaders({ 'Content-Type': 'application/json' }),
  });

  return response;
}

class OpsStatusService {
  /**
   * Most recent pipeline for a project, most-recently-updated first.
   * @param {string} projectId - GitLab project path (e.g. "group/subgroup/repo")
   * @returns {Promise<object|null>} Pipeline summary, or null if none exist
   */
  async getLatestPipeline(projectId) {
    const response = await gitlabGet('/workflows', {
      project_id: projectId,
      per_page: '1',
      order_by: 'updated_at',
    });

    if (response.status === 401) {
      throw new Error('not_authenticated');
    }
    if (!response.ok) {
      throw new Error(`pipeline_fetch_failed:${response.status}`);
    }

    const pipelines = await response.json();
    return Array.isArray(pipelines) && pipelines.length > 0 ? pipelines[0] : null;
  }

  /**
   * Vulnerability findings summary (GitLab Ultimate Security Dashboard).
   * Degrades to { available: false } when the BFF reports the tier/plan
   * doesn't support it — never throws for that case.
   * @param {string} projectId
   * @returns {Promise<{available: boolean, counts: object, total: number}>}
   */
  async getVulnerabilitySummary(projectId) {
    const response = await gitlabGet('/vulnerabilities', { project_id: projectId });

    if (response.status === 401) {
      throw new Error('not_authenticated');
    }
    if (!response.ok) {
      devLog('⚠️ Vulnerability findings request failed, treating as unavailable', { projectId, status: response.status });
      return { available: false, counts: {}, total: 0 };
    }

    const data = await response.json();
    if (!data.available) {
      return { available: false, counts: {}, total: 0 };
    }

    const counts = { critical: 0, high: 0, medium: 0, low: 0, info: 0, unknown: 0 };
    for (const finding of data.findings || []) {
      const severity = (finding.severity || 'unknown').toLowerCase();
      counts[severity] = (counts[severity] || 0) + 1;
    }

    return {
      available: true,
      counts,
      total: (data.findings || []).length,
    };
  }

  /**
   * SBOM stand-in: GitLab's Dependency List (also Ultimate-tier). Reports
   * only availability + component count — building/exporting a real
   * CycloneDX SBOM is out of scope for this page.
   * @param {string} projectId
   * @returns {Promise<{available: boolean, count: number}>}
   */
  async getSbomStatus(projectId) {
    const response = await gitlabGet('/dependencies', { project_id: projectId });

    if (response.status === 401) {
      throw new Error('not_authenticated');
    }
    if (!response.ok) {
      devLog('⚠️ Dependency list request failed, treating as unavailable', { projectId, status: response.status });
      return { available: false, count: 0 };
    }

    const data = await response.json();
    if (!data.available) {
      return { available: false, count: 0 };
    }

    return { available: true, count: (data.dependencies || []).length };
  }
}

const opsStatusService = new OpsStatusService();
export default opsStatusService;
